/* eslint-disable prettier/prettier */
import { useState, useEffect } from 'react';

import Accordion from './Accordion';
import LayoutContainer from './LayoutContainer';

import icBars from '@/assets/ic_bars.svg';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { AccordionItem, FaqSectionProps } from '@/types/faq';

const Scores = ({ state, title }: FaqSectionProps) => {
  const { isMobile } = useIsDesktop();
  const [activeAccordion, setActiveAccordion] = useState<string | null>(
    state?.accordionId || null
  );

  // Actualizar el acordeón activo cuando cambia el state
  useEffect(() => {
    if (state?.accordionId) {
      setActiveAccordion(state.accordionId);
    }
  }, [state]);

  // Manejador para alternar acordeones
  const toggleAccordion = (id: string) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  // Datos para los acordeones
  const accordionItems: AccordionItem[] = [
    {
      id: 'puntajes-como-suman-jugadores',
      title: '¿Cómo se suman los puntos de mi equipo?',
      content: (
        <div className="text-sm">
          <p>
            El puntaje del Equipo de cada participante es la suma de los puntos
            obtenidos por todos los futbolistas titulares (o los suplentes que
            jueguen en lugar de los titulares). Ese puntaje se calculará del
            siguiente modo:
          </p>

          <ol className="list-decimal pl-6 mt-4 marker:text-blue-700 marker:font-bold">
            <li className="mb-2">
              <span>
                Clarín calificará cada fecha, en la sección de Deportes, a todo
                futbolista que haya jugado al menos 20 minutos del tiempo
                reglamentario (No serán tenidos en cuenta los minutos que un
                futbolista juegue en tiempo suplementario o de descuento).
              </span>
            </li>
            <li className="mb-2">
              <span>
                A este puntaje básico, denominado Calificación Clarín, se le
                sumarán, eventualmente, puntos adicionales por: goles
                convertidos, penales atajados, valla invicta y por ser la figura
                de la cancha.
              </span>
            </li>
            <li className="mb-2">
              <span>
                También, eventualmente, se le restarán puntos por goles
                recibidos, goles convertidos en contra de su propia valla,
                penales errados y por recibir tarjetas amarillas y/o rojas.
              </span>
            </li>
          </ol>

          <p className="mt-4 mb-4">
            Este es el detalle de la asignación de puntos:
          </p>

          <div className="flex flex-row justify-between font-extrabold">
            <span className="p-3">Calificación Clarín</span>
            <span className="p-3 text-right">+1 a 10 puntos</span>
          </div>

          <div className="w-full overflow-hidden">
            <table className="w-full bg-gray-100 rounded-md overflow-hidden">
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">Capitán</td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    Duplica Calificación Clarín
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Figura de la cancha
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    +4 puntos
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Gol convertido de visitante
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    +2 puntos por gol (es indiferente la posición)
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Gol convertido (de jugada)
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    +4 puntos para el delantero
                    <br />
                    +6 puntos para el volante
                    <br />
                    +9 puntos para el defensor
                    <br />
                    +12 puntos para el arquero
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Gol de penal
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    +3 puntos (es indiferente la posición)
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Gol en contra
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    -2 puntos (es indiferente la posición)
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Gol recibido (de jugada o penal)
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    -1 punto para el arquero
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Valla invicta
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    +3 puntos para el arquero
                    <br />
                    +2 puntos para el defensor
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Tarjeta amarilla
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    -2 puntos
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Tarjeta roja
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    -4 puntos
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Penal errado
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    -4 puntos
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-3 text-blue-700 font-medium">
                    Penal atajado
                  </td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    +4 puntos
                  </td>
                </tr>
                <tr>
                  <td className="p-3 text-blue-700 font-medium">Gol de Oro</td>
                  <td className="p-3 text-right text-blue-700 font-medium">
                    +5 puntos
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-4">
            Cuando un futbolista titular no juegue al menos 20 minutos del
            tiempo reglamentario, el suplente de su mismo puesto lo reemplazará
            automáticamente. Si el suplente de ese puesto tampoco juega al menos
            20 minutos del tiempo reglamentario, el equipo no sumará puntos por
            ninguno de los dos futbolistas.
          </p>

          <p className="mt-4">
            Es importante aclarar que si el futbolista titular que no juega es
            el capitán del equipo, el suplente que juegue en su lugar NO actuará
            como capitán, es decir, no duplicará su Calificación Clarín.
          </p>
        </div>
      ),
    },
    {
      id: 'puntajes-como-suma-capitan',
      title: '¿Cómo suma mi capitán?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            El jugador designado como Capitán del Equipo en la fecha duplicará
            sólo la Calificación Clarín (que va del 1 al 10) para luego sumarle
            y/o restarle las incidencias del jugador en el partido, según
            corresponda.
          </p>
          <br />
          <p>
            Tené en cuenta que si tu Capitán no suma puntos en la fecha, el
            suplente que lo reemplaza no duplica su Calificación Clarín.
          </p>
          <br />
          <p>
            Ejemplo: Si elegís al delantero Pablo Gómez como Capitán de la Fecha
            5, y ese jugador recibe una Calificación Clarín de 7 pts., hace un
            gol y recibe una tarjeta amarilla, el volante Gómez sumaría de la
            siguiente manera:
          </p>
          <br />
          <ul className="list-disc pl-6 marker:text-black">
            <li>7 pts. de Calificación Clarín.</li>
            <li>x2, por ser Capitán = 14 pts.</li>
            <li>+4 por el gol de delantero</li>
            <li>-2 por la tarjeta amarilla = 18 pts. de puntaje final</li>
          </ul>
        </div>
      ),
    },
    {
      id: 'puntajes-que-es-un-recalculo',
      title: '¿Qué es y cuándo hay recálculo de puntos?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            Un recálculo es cuando se vuelven a contabilizar todos los puntajes
            de la fecha. Puede afectar al rendimiento de tu equipo, tu posición
            en la tabla de la fecha, el ranking general y en los Torneos de
            Amigos.
          </p>
          <br />
          <p>
            Los recálculos suceden cuando un partido que había sido postergado
            se juega o cuando se produce algún error u omisión en la
            calificación de un jugador. Por ejemplo, si un jugador fue
            amonestado pero por algún inconveniente esa amarilla no fue tenida
            en cuenta durante el cálculo, se realizará un recálculo y se
            modificará el puntaje correspondiente.
          </p>
        </div>
      ),
    },
    {
      id: 'puntajes-que-sucede-si-empatan',
      title: '¿Qué sucede en caso de empate en puntos entre dos o más equipos?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            En caso de que dos o más equipos resulten ganadores por igual suma
            de puntos, se recurrirá, respetando el orden, a los siguientes
            mecanismos de desempate:
          </p>
          <ol className="list-decimal pl-6 mt-4 marker:text-blue-700 marker:font-bold">
            <li className="mb-2">
              <span>Equipo con menor cotización total de los jugadores.</span>
            </li>
            <li className="mb-2">
              <span>
                Equipo con más puntos de calificación del Diario Clarín
                solamente (&ldquo;Calificación Clarín&ldquo;).
              </span>
            </li>
            <li className="mb-2">
              <span>Quien tenga el menor número de orden de inscripción.</span>
            </li>
          </ol>
        </div>
      ),
    },
    {
      id: 'puntajes-donde-ver-calificaciones',
      title:
        '¿Dónde puedo ver las calificaciones que asigna el diario Clarín a mis jugadores?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            Las calificaciones que otorga Clarín en cada fecha
            (&ldquo;Calificación Clarín&ldquo;), podrán chequearse en la versión
            impresa del diario, no así en la versión digital (Clarin.com). Podés
            ver la Calificación Clarín de tus jugadores en Mi Equipo, ingresando
            a Puntajes de mis jugadores. Además, podés ver la puntuación
            desglosada de todos los jugadores (Calificación Clarín + Incidencias
            del partido) en el Ayudante de Campo, en Puntajes de la última
            fecha.
          </p>
        </div>
      ),
    },
    {
      id: 'puntajes-que-sucede-con-postergados',
      title: '¿Qué sucede con los partidos postergados o suspendidos?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            Un partido postergado es aquel que no se jugó, o se jugaron menos de
            20 minutos. Un partido suspendido, en cambio, es aquel en el que se
            jugaron más de 20 minutos y luego se suspendió.
          </p>
          <br />
          <ul className="list-disc pl-6 marker:text-black">
            <li>
              Los partidos postergados no sumarán para el Ranking de la fecha
              pero sí serán tenidos en cuenta para el Ranking general, siempre y
              cuando se jueguen después de la fecha y antes de la finalización
              del torneo.
            </li>
            <li>
              Los partidos suspendidos serán tenidos en cuenta para la sumatoria
              de puntos de la fecha. Si el partido se reanudase en fechas
              posteriores a la publicación de puntos de la fecha, las
              incidencias de los minutos restantes no serán tenidos en cuenta
              para Gran DT.
            </li>
          </ul>
        </div>
      ),
    },
  ];

  return (
    <LayoutContainer>
      <div className="flex gap-x-3 items-center content-center mb-8">
        {isMobile && (
          <img
            alt="Gran DT - logo calendario"
            className="w-6 h-6"
            src={icBars}
          />
        )}
        <span className={`text-lg text-[#333333] font-bold`}>{title}</span>
      </div>

      {accordionItems.map((item) => (
        <Accordion
          key={item.id}
          isOpen={activeAccordion === item.id}
          title={item.title}
          onToggle={() => toggleAccordion(item.id)}
        >
          {item.content}
        </Accordion>
      ))}
    </LayoutContainer>
  );
};

export default Scores;
